/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { ChangeDetectionStrategy, Component, input, output, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslatePipe } from '@ngx-translate/core';

export type PageState = 'loading' | 'empty' | 'error' | 'unauthorized' | 'not-found';

const DEFAULT_ICONS: Record<PageState, string> = {
  loading: 'refresh',
  empty: 'folder_open',
  error: 'error_outline',
  unauthorized: 'block',
  'not-found': 'search_off'
};

/**
 * Shared page-state primitive for loading, empty, error, unauthorized and not-found states.
 * Uses Material/ADF building blocks and consumes the global ACA design tokens.
 */
@Component({
  selector: 'aca-page-state',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule, MatProgressSpinnerModule, TranslatePipe],
  templateUrl: './page-state.component.html',
  styleUrls: ['./page-state.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'aca-page-state' }
})
export class PageStateComponent {
  state = input<PageState>('empty');
  title = input<string>('');
  message = input<string>('');
  icon = input<string>('');
  actionLabel = input<string>('');

  action = output<void>();

  resolvedIcon(): string {
    return this.icon() || DEFAULT_ICONS[this.state()];
  }

  resolvedRole(): string {
    return this.state() === 'error' || this.state() === 'unauthorized' ? 'alert' : 'status';
  }

  onAction(): void {
    this.action.emit();
  }
}
